import React, {
    useRef,
    useState,
    useEffect,
    useCallback,
    forwardRef,
} from 'react';
import { Trans } from '@lingui/macro';
import {
    Box,
    Text,
    useVisible,
    useBreakpoint,
    Flex,
    Paragraph,
    Skeleton,
} from '@landr/maestro';
import AboutItem from 'components/AboutItem';
import { Pack, Repack } from 'types/generated/graphql';
import sliceToLastWord from '../helpers/sliceToLastWord';
import getMaxAllowedCharacters, {
    DesktopNumbersOfLines,
    MobileNumbersOfLines,
} from '../helpers/getMaxAllowedCharacters';

const useDescription = ({
    description: fullDescription,
    isMobile,
}: {
    description: Pack['description'] | Repack['description'];
    isMobile: boolean;
}) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [showReadMore, setShowReadMore] = useState(false);

    const updateDescriptionSliced = useCallback(() => {
        const maxLength = getMaxAllowedCharacters(ref, isMobile);
        const [isSliced] = sliceToLastWord(maxLength, fullDescription);

        setShowReadMore(isSliced);
    }, [isMobile, fullDescription]);

    useEffect(() => {
        updateDescriptionSliced();
        window.addEventListener('resize', updateDescriptionSliced);

        return () =>
            window.removeEventListener('resize', updateDescriptionSliced);
    }, [updateDescriptionSliced]);

    return { ref, showReadMore };
};

export const _Description = forwardRef(
    (
        {
            fullDescription = '',
            showReadMore,
            title,
            visible,
            hide,
            show,
            isMobile,
        }: {
            fullDescription?: string;
            showReadMore: boolean;
            title: string;
            visible: boolean;
            isMobile: boolean;
            hide: () => void;
            show: () => void;
        },
        ref: React.Ref<HTMLDivElement>,
    ) => (
        <>
            <Box
                ref={ref}
                display="flex"
                flexDirection="column"
                textAlign="left"
                position="relative"
                width="100%"
            >
                <Flex
                    flexDirection="column"
                    maxHeight="105px"
                    onClick={show}
                    cursor="pointer"
                >
                    <Box
                        nbOfLines={
                            isMobile
                                ? MobileNumbersOfLines
                                : DesktopNumbersOfLines
                        }
                    >
                        <Paragraph color="text.medium">
                            {fullDescription}
                        </Paragraph>
                    </Box>
                    {showReadMore && (
                        <Text
                            textStyle="body"
                            fontWeight="bold"
                            display="flex"
                            flexDirection="column"
                            marginTop="2xs"
                        >
                            <Trans>Read more</Trans>
                        </Text>
                    )}
                </Flex>
                <AboutItem
                    visible={visible}
                    hide={hide}
                    title={title}
                    description={fullDescription}
                />
            </Box>
        </>
    ),
);

const DescriptionLoaded = ({
    title,
    description,
}: {
    title: Pack['name'] | Repack['name'];
    description: Pack['description'] | Repack['description'];
}) => {
    const isMobile = useBreakpoint('md');
    const { visible, hide, show } = useVisible();
    const { ref, showReadMore } = useDescription({
        description: description,
        isMobile: isMobile,
    });

    return (
        <_Description
            ref={ref}
            fullDescription={description}
            showReadMore={showReadMore}
            title={title ?? ''}
            visible={visible}
            isMobile={isMobile}
            hide={hide}
            show={show}
        />
    );
};

interface CommonProps {
    isLoading?: boolean;
}

interface LoadingProps extends CommonProps {
    isLoading: true;
    title?: never;
    description?: never;
}

interface LoadedProps extends CommonProps {
    isLoading?: boolean;
    title: Pack['name'] | Repack['name'];
    description: Pack['description'] | Repack['description'];
}

const Description = (props: LoadingProps | LoadedProps) => {
    if (props.isLoading) {
        return (
            <Flex flexDirection="column" width="100%" mt="xl">
                <Skeleton height="20px" width="90%" mb="xs" />
                <Skeleton height="20px" width="90%" />
            </Flex>
        );
    }

    const { title, description } = props;

    return <DescriptionLoaded title={title} description={description} />;
};

Description.displayName = 'PageHeader.Description';

export default Description;
